import React from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import { useLoaderData } from "react-router-dom";

import PageHeader from "~/components/page-header";
import { axios, useAlert } from "~/utils";

// The row that stands for every Monitored App, present and future. The API
// spells it as a null app; the page needs a string to key state by.
const ALL_APPS = "all";

function keyOf(monitoredApp) {
  return monitoredApp === null ? ALL_APPS : String(monitoredApp);
}

function toFlagMap(subscriptions) {
  return Object.fromEntries(
    subscriptions.map((s) => [
      keyOf(s.monitoredApp),
      { notifyThread: s.notifyThread, notifyDm: s.notifyDm },
    ])
  );
}

const OFF = { notifyThread: false, notifyDm: false };

function flagsFor(flagMap, key) {
  return flagMap[key] || OFF;
}

function isSubscribed(flagMap, key) {
  const flags = flagsFor(flagMap, key);
  return flags.notifyThread || flags.notifyDm;
}

// Only the rows the user actually moved are sent, so a save never rewrites a
// row somebody left alone.
function changedKeys(saved, draft) {
  const keys = new Set([...Object.keys(saved), ...Object.keys(draft)]);
  return [...keys].filter((key) => {
    const before = flagsFor(saved, key);
    const after = flagsFor(draft, key);
    return (
      before.notifyThread !== after.notifyThread ||
      before.notifyDm !== after.notifyDm
    );
  });
}

// Apps the user is subscribed to stay visible; the rest appear once the search
// matches them, because the full list is too long to browse. A row the user
// just turned off is still in `saved`, so it stays on screen until the save
// goes through instead of vanishing under the cursor.
function visibleApps(apps, saved, draft, search) {
  const term = search.trim().toLowerCase();
  return apps.filter((app) => {
    const key = keyOf(app.id);
    return (
      isSubscribed(saved, key) ||
      isSubscribed(draft, key) ||
      (term !== "" && app.appName.toLowerCase().includes(term))
    );
  });
}

function SubscriptionsPage() {
  const { apps, subscriptions } = useLoaderData();
  const [saved, setSaved] = React.useState(() => toFlagMap(subscriptions));
  const [draft, setDraft] = React.useState(saved);
  const [search, setSearch] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);
  const addAlert = useAlert();

  const pending = changedKeys(saved, draft);

  const toggle = (key, field) =>
    setDraft((current) => {
      const flags = flagsFor(current, key);
      return { ...current, [key]: { ...flags, [field]: !flags[field] } };
    });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await axios({
        method: "POST",
        url: "api/v1/release-coverage/subscriptions",
        data: {
          subscriptions: pending.map((key) => ({
            monitoredApp: key === ALL_APPS ? null : Number(key),
            ...flagsFor(draft, key),
          })),
        },
      });
      const stored = toFlagMap(response.data);
      setSaved(stored);
      setDraft(stored);
      addAlert("Subscriptions saved", "success");
    } catch (e) {
      const detail = e.response && e.response.data && e.response.data.detail;
      setError(
        detail || "Could not save your subscriptions. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    { key: ALL_APPS, name: "All apps", muted: "every Monitored App" },
    ...visibleApps(apps, saved, draft, search).map((app) => ({
      key: keyOf(app.id),
      name: app.appName,
      muted: "",
    })),
  ];

  return (
    <>
      <PageHeader>
        <Button disabled={saving || pending.length === 0} onClick={save}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </PageHeader>
      <p className="text-muted">
        Choose how you want to hear about release event coverage. In thread
        mentions you under the Release message; DM sends you a direct message
        when something new is found.
      </p>
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      <Form.Control
        className="mb-3"
        type="search"
        placeholder="Search apps…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Table striped hover>
        <thead>
          <tr>
            <th scope="col">App</th>
            <th scope="col">In thread</th>
            <th scope="col">DM</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>
                {row.name}
                {row.muted && (
                  <span className="text-muted ms-2">{row.muted}</span>
                )}
              </td>
              <td>
                <Form.Check
                  type="switch"
                  aria-label={`Mention ${row.name} in thread`}
                  checked={flagsFor(draft, row.key).notifyThread}
                  onChange={() => toggle(row.key, "notifyThread")}
                />
              </td>
              <td>
                <Form.Check
                  type="switch"
                  aria-label={`DM me about ${row.name}`}
                  checked={flagsFor(draft, row.key).notifyDm}
                  onChange={() => toggle(row.key, "notifyDm")}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {rows.length === 1 && (
        <p className="text-muted">
          Search for an app above to subscribe to it, or turn on “All apps”.
        </p>
      )}
    </>
  );
}

SubscriptionsPage.loader = async function loader() {
  const [appsResponse, subscriptionsResponse] = await Promise.all([
    axios("api/v1/release-coverage/monitored-apps"),
    axios("api/v1/release-coverage/subscriptions"),
  ]);
  return {
    apps: appsResponse.data,
    subscriptions: subscriptionsResponse.data,
  };
};

export default SubscriptionsPage;
